import {forwardRef, ReactNode, Ref, useMemo} from 'react';
import './flat-select-date.scss';
import {Box, FormControl, IconButton, InputAdornment, Slide} from '@mui/material';
import {MobileDatePicker, MobileDatePickerProps} from '@mui/x-date-pickers';
import moment from 'moment';
import {MdOutlineCalendarMonth} from 'react-icons/md';
import {MdClose} from 'react-icons/md';
import {Pallete} from '../../theme/bluebird';
import FlatInput from '../flat-input/flat-input';
import {titleCase} from '../../utils/functions';
import {translations} from '../../i18n/translations';
import {useTranslation} from 'react-i18next';

interface FlatSelectDateProps extends MobileDatePickerProps<Date> {
  id?: string;
  label?: string;
  required?: boolean;
  errors?: string;
  readonly?: boolean;
  allowClear?: boolean;
  placeholder?: string;
  disableFuture?: boolean;
  disablePast?: boolean;
  disabledTooltip?: ReactNode;
  noUtc?: boolean;
}

export const FlatSelectDate = forwardRef(
  (
    {
      label,
      required,
      disabled,
      readonly,
      errors,
      allowClear,
      placeholder,
      disableFuture,
      disablePast,
      disabledTooltip,
      noUtc,
      ...rest
    }: FlatSelectDateProps,
    ref: Ref<HTMLInputElement>
  ) => {
    const {t} = useTranslation();
    const hasError = !!errors;
    const date = useMemo(
      () => (rest.value ? (noUtc ? moment(rest.value) : moment.utc(rest.value)) : null),
      [rest.value, noUtc]
    );

    return (
      <FormControl className="flat-select-date" sx={{width: '100%', ...rest.sx}}>
        <MobileDatePicker
          {...rest}
          // @ts-ignore
          value={date}
          localeText={{
            cancelButtonLabel: t(translations.modal.cancel),
            okButtonLabel: t(translations.modal.confirm),
          }}
          slotProps={{
            toolbar: {
              // @ts-ignore
              toolbarTitle: t(translations.care_and_activities.handle_task_modal.select_date),
            },
          }}
          slots={{
            textField: ({value, onChange, onBlur, onClick, inputProps}) => (
              <FlatInput
                {...{
                  value,
                  onChange,
                  onBlur,
                  readonly,
                  disabled,
                  disabledTooltip,
                  label,
                  required,
                }}
                ref={ref}
                onClick={!readonly ? onClick : undefined}
                inputProps={{
                  ...inputProps,
                  value: date
                    ? date
                        .format('DD MMM YYYY')
                        .split(' ')
                        .map((d) => titleCase(d))
                        .join(' ')
                    : undefined,
                }}
                placeholder={placeholder}
                InputProps={{
                  readOnly: readonly,
                  endAdornment: (
                    <Box
                      sx={{
                        display: 'flex',
                      }}
                    >
                      {allowClear && !readonly && !required && rest.value && (
                        <InputAdornment position="end">
                          <IconButton
                            component="div"
                            aria-label="calendar"
                            size="small"
                            onClick={(e) => {
                              // Prevent the click event from bubbling up
                              e.stopPropagation();
                              e.preventDefault();

                              // Clear the date
                              if (rest && rest.onChange) {
                                rest.onChange(null, {
                                  validationError: null,
                                });
                              }
                            }}
                          >
                            <MdClose />
                          </IconButton>
                        </InputAdornment>
                      )}
                      <InputAdornment position="end" disablePointerEvents={disabled}>
                        {!readonly && (
                          <IconButton
                            component="div"
                            aria-label="calendar"
                            size="small"
                            onClick={onClick}
                          >
                            <MdOutlineCalendarMonth />
                          </IconButton>
                        )}
                      </InputAdornment>
                    </Box>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-input': {
                    cursor: 'pointer !important',
                  },
                }}
              />
            ),
          }}
          disableFuture={disableFuture}
          disablePast={disablePast}
          disabled={disabled}
        />
        <Slide direction="left" in={hasError} mountOnEnter unmountOnExit>
          <span className="input-error" style={{color: Pallete.red.base}}>
            {errors}
          </span>
        </Slide>
      </FormControl>
    );
  }
);
